import type { UseQueryOverrideOptions } from '../../../types'
import { onNuxtReady, useCoreLogger, useQuery, useXmpp, useUserInfo, useNuxtApp, useQueryClient, hasSessionCookie } from '#imports'

export interface SeamlessWalletAccountDetailsHostedWallets {
    wallets: any[]
    total_balance: string
    total_count: number
}

export interface SeamlessWalletAccountDetailsWallets {
    BONUS: string
    CASH: string
    CASH_WITH_BONUS_TERMS: string
}

export interface SeamlessWalletAccountDetailsResponse {
    balance: string
    game_wallet_type: 'HOSTED' | 'SEAMLESS'
    hosted_wallets: SeamlessWalletAccountDetailsHostedWallets
    status: string
    wagering_requirement: any
    wagering_requirements: any[]
    wagering_requirement_wallets?: Record<string, SeamlessWalletAccountDetailsWallets>
    wallets: SeamlessWalletAccountDetailsWallets
    withdrawable_amount: string
}

export type UseSeamlessWalletAccountDetailsOptions = Omit<UseQueryOverrideOptions, 'enabled'>

export const useSeamlessWalletAccountDetails = (options: UseSeamlessWalletAccountDetailsOptions = {}) => {
    const { isLoggedIn } = useUserInfo()
    const nuxtApp = useNuxtApp()
    const queryClient = useQueryClient()

    if (import.meta.client && !nuxtApp._seamlessWalletXmppRegistered) {
        nuxtApp._seamlessWalletXmppRegistered = true

        // Attached for the app's life, not through `useXmppListener`: that detaches with the component
        // that called first, usually the default layout, which unmounts on the way into playgame, and
        // the flag keeps every later caller from attaching another.
        onNuxtReady(() => nuxtApp.runWithContext(() => {
            const logger = useCoreLogger().withTag('useSeamlessWalletAccountDetails')

            return useXmpp().ensureInit().then((xmpp) => {
                xmpp?.on({ kind: 'message', xmlns: 'cego:UserBalanceUpdated' }, () => {
                    // A refetch runs past `enabled`. The session cookie, unlike `isLoggedIn`, follows a
                    // session ended in another tab.
                    if (!hasSessionCookie()) return

                    queryClient.refetchQueries({ key: 'seamlessWallet:account_details' })
                })
            }).catch(error => logger.warn(error))
        }))
    }

    return useQuery<SeamlessWalletAccountDetailsResponse | undefined>('/gateway/seamless-wallet/api/public/v1/account/details', {
        lazy: true,
        server: false,
        ...options,
        key: 'seamlessWallet:account_details',
        enabled: isLoggedIn,
        service: 'seamlessWallet',
    })
}
